import { msg } from "@lingui/core/macro";
import {
  createContext,
  createEffect,
  createMemo,
  type JSX,
  onMount,
  type ParentComponent,
  untrack,
  useContext,
} from "solid-js";
import { fromActorRef, useActorRef } from "@fleet/state-machines/solid";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import {
  appBootMachine,
  createProfileHandlers,
  handleApiResponse,
  isSignOutDialogOpen,
  isSignOutInProgress,
  queryKeys,
  type Session,
  signOutMachine,
} from "@fieldcheck/shared";
import { useLocation, useNavigate } from "@tanstack/solid-router";
import { usePowerSyncContext } from "./power-sync-context";
import { useI18n } from "./i18n-provider";
import { useExecuteSignOut, useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { showToast } from "@/components/ui/toast";
import { createRevokeSessionHandlers } from "./revoke-session-handlers";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

const AUTH_PATH_PREFIXES = ["/login", "/register", "/forgot-password", "/reset-password"];

function isInAuthGroup(pathname: string): boolean {
  return AUTH_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Context to expose sign out handlers to the rest of the app.
 */
interface SignOutContextValue {
  promptSignOut: () => void;
  promptQuickSignOut: () => void;
  promptClearData: () => void;
  promptRevokeSession: (sessionId: string) => void;
  isChecking: () => boolean;
  isSigningOut: () => boolean;
  /**
   * P23/CC-60 · assignment C: the LAST revocation failure message (`null` if
   * there was none). The session list renders it: without this, a 500 closed the
   * dialog without saying anything and the device remained signed in.
   */
  revokeError: () => string | null;
}

const SignOutContext = createContext<SignOutContextValue>();

export function useSignOut() {
  const context = useContext(SignOutContext);
  if (!context) {
    throw new Error("useSignOut must be used within SignOutProvider (BootManager)");
  }
  return context;
}

/**
 * BootManager coordinates the loading state of the entire application
 * using appBootMachine from @fieldcheck/shared.
 *
 * It acts as a gatekeeper that only renders the application content
 * once all parallel boot processes (DB, I18n, Fonts, Session) are ready.
 */
export const BootManager: ParentComponent = (props) => {
  const actorRef = useActorRef(appBootMachine);
  const state = fromActorRef(actorRef);

  const ps = usePowerSyncContext();
  const session = useSession();
  const executeSignOut = useExecuteSignOut();
  const location = useLocation();
  const navigate = useNavigate();

  // 1. Database Readiness
  createEffect(() => {
    if (!ps.isLoading() && ps.db()) {
      actorRef.send({ type: "DB_READY" });
    }
  });

  // 2. Resolve non-blocking web resources immediately on mount
  onMount(() => {
    actorRef.send({ type: "I18N_READY" });
    actorRef.send({ type: "FONTS_LOADED" });
    actorRef.send({ type: "TYPOGRAPHY_READY" });
    actorRef.send({ type: "THEME_READY" });
  });

  // 5. Session & Auth Group Detection
  createEffect(() => {
    const { isPending } = session;
    const hasSession = session.isAuthenticated();
    const loc = location();
    // Multi-account: `/login?mode=add` is reached BY an already-authenticated
    // user who wants to add a second account. Treat it as NOT in the auth group
    // so the boot machine's "authenticated + in auth group → redirect home" rule
    // doesn't bounce them out of the add-account login before they can sign in.
    const isAddingAccount = new URLSearchParams(loc.searchStr ?? "").get("mode") === "add";
    const inAuthGroup = !isAddingAccount && isInAuthGroup(loc.pathname);

    if (!isPending) {
      actorRef.send({
        type: "SESSION_RESOLVED",
        hasSession,
        inAuthGroup,
      });
    }
  });

  // 6. Centralized Redirection (authenticated user on an auth page → app).
  createEffect(() => {
    if (state().matches("redirecting") && untrack(() => session.isAuthenticated())) {
      void navigate({ to: "/" });
    }
  });

  const isReady = () => state().matches("ready") || state().matches("redirecting");

  return (
    <SignOutProvider
      db={ps.db()}
      session={session.data}
      disconnectAndClear={ps.disconnectAndClear}
      isReady={isReady()}
      onRedirectToSignIn={() => {
        executeSignOut();
      }}
    >
      {props.children}
    </SignOutProvider>
  );
};

function SignOutProvider(props: {
  db: AbstractPowerSyncDatabase | null;
  session: Session | null | undefined;
  disconnectAndClear: () => Promise<void>;
  isReady: boolean;
  onRedirectToSignIn: () => void;
  children: JSX.Element;
}) {
  const { t } = useI18n();

  const signOutInput = createMemo(() => ({
    db: props.db,
    session: props.session,
    // On web, useExecuteSignOut (triggered by onRedirectToSignIn) handles the
    // database cleanup and server sign-out in a non-blocking background task.
    // We pass no-ops here to prevent duplicate execution, database contention
    // and network/timeout races.
    disconnectAndClear: async () => {
      // no-op on web: handled by executeSignOut
    },
    onSignOut: async () => {
      // no-op on web: handled by executeSignOut
    },
    onRedirectToSignIn: props.onRedirectToSignIn,
    // P23/CC-60 · assignment C (SECURITY). `api.POST` is `openapi-fetch`: it NEVER
    // throws, it returns `{data, error}`. Without `handleApiResponse`, a 500 passed
    // as success and the UI closed the dialog without a single warning while the
    // device REMAINED signed in.
    ...createRevokeSessionHandlers({
      revoke: (sessionId) =>
        handleApiResponse(api.POST("/api/auth/revoke-session", { body: { id: sessionId } })),
      invalidateSessions: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.auth.activeSessions });
      },
      notifyRevokeFailed: () => {
        showToast(t(msg`Could not revoke the session. The device is still signed in.`), "error");
      },
    }),
  }));

  const signOutActor = useActorRef(signOutMachine, {
    input: untrack(() => signOutInput()),
  });

  const signOutState = fromActorRef(signOutActor);
  const profileHandlers = createProfileHandlers(signOutActor.send);

  // The «Sign Out?» dialog opens ONLY in its own states (see
  // `isSignOutDialogOpen`): with `isSigningOut()` it also opened during
  // `revoking` and `clearing`, stacking over the dialog for those flows.
  const isSignOutDialog = () => isSignOutDialogOpen(signOutState());
  const isSignOutExecuting = () =>
    signOutState().matches("executing") || signOutState().matches("executingForce");
  const isClearConfirm = () => signOutState().matches("confirmClear");
  const isRevokeConfirm = () => signOutState().matches("confirmRevoke");
  const isChecking = () => signOutState().matches("checkingUnsynced");
  const isSigningOut = () => isSignOutInProgress(signOutState());
  const hasUnsyncedChanges = () => signOutState().context.hasUnsyncedChanges;

  const confirmSignOut = () => {
    signOutActor.send({ type: "CONFIRMED" });
  };

  const cancelSignOut = () => {
    signOutActor.send({ type: "CANCELLED" });
  };

  return (
    <SignOutContext.Provider
      value={{
        promptSignOut: profileHandlers.promptSignOut,
        promptQuickSignOut: profileHandlers.promptQuickSignOut,
        promptClearData: profileHandlers.promptClearData,
        promptRevokeSession: profileHandlers.promptRevokeSession,
        isChecking,
        isSigningOut,
        revokeError: () => signOutState().context.revokeError,
      }}
    >
      {props.children}

      <ConfirmDialog
        open={isSignOutDialog()}
        onOpenChange={(open) => {
          if (!open && !isSignOutExecuting()) {
            cancelSignOut();
          }
        }}
        title={hasUnsyncedChanges() ? t(msg`Unsynced Changes`) : t(msg`Sign Out?`)}
        description={
          hasUnsyncedChanges()
            ? t(
                msg`You have changes that haven't been synced to the server yet. If you sign out now, these changes will be permanently lost.`,
              )
            : t(msg`Are you sure you want to sign out on this device?`)
        }
        confirmLabel={hasUnsyncedChanges() ? t(msg`Sign Out Anyway`) : t(msg`Sign Out`)}
        variant="destructive"
        onConfirm={confirmSignOut}
        isLoading={isSignOutExecuting()}
      />

      <ConfirmDialog
        open={isClearConfirm() || signOutState().matches("clearing")}
        onOpenChange={(open) => {
          if (!open && !signOutState().matches("clearing")) {
            cancelSignOut();
          }
        }}
        title={t(msg`Clear local data`)}
        description={t(
          msg`This will delete all your local tasks and lists. This action cannot be undone. Continue?`,
        )}
        confirmLabel={t(msg`Delete Everything`)}
        variant="destructive"
        onConfirm={confirmSignOut}
        isLoading={signOutState().matches("clearing")}
      />

      <ConfirmDialog
        open={isRevokeConfirm() || signOutState().matches("revoking")}
        onOpenChange={(open) => {
          if (!open && !signOutState().matches("revoking")) {
            cancelSignOut();
          }
        }}
        title={t(msg`Revoke Session`)}
        description={t(msg`This will sign out the device immediately. Continue?`)}
        confirmLabel={t(msg`Revoke`)}
        variant="destructive"
        onConfirm={confirmSignOut}
        isLoading={signOutState().matches("revoking")}
      />
    </SignOutContext.Provider>
  );
}
